import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CameraIcon } from '@heroicons/react/24/outline';
import { useImageUpload } from '@shared/dream-components';
import cn from 'classnames';

import { Avatar } from './ui/Avatar';

interface ProfileAvatarPickerProps {
  currentImageUrl?: string;
  onFileSelect: (file: File) => void;
  disabled?: boolean;
  isDefaultAvatar?: boolean;
}

export const ProfileAvatarPicker: FC<ProfileAvatarPickerProps> = ({
  currentImageUrl,
  onFileSelect,
  disabled,
  isDefaultAvatar,
}) => {
  const { t } = useTranslation();
  const { previewUrl, open, inputProps } = useImageUpload({ onFileSelect, disabled });
  const showPrompt = Boolean(isDefaultAvatar) && !previewUrl;

  return (
    <div className="w-full space-y-1">
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={open}
          disabled={disabled}
          // The wide button keeps the avatar at its size while giving touch devices a target that spans the form.
          className="group flex h-[108px] w-full items-center justify-center rounded-lg disabled:cursor-not-allowed disabled:opacity-50 md:w-[108px] md:rounded-full"
          aria-label={
            showPrompt ? t('community.profile_identity.avatar_add') : t('community.profile_identity.avatar_change')
          }
        >
          <span
            className={cn('relative size-[108px] overflow-hidden rounded-full transition-colors', {
              'border-2 border-dashed border-gray-300 bg-gray-50 p-1 md:group-hover:border-gray-400 md:group-hover:bg-gray-100':
                showPrompt,
            })}
          >
            <Avatar
              src={previewUrl ?? currentImageUrl}
              alt={t('community.profile_identity.avatar_alt')}
              className={cn('size-full', { 'opacity-50': showPrompt })}
            />
            {/* Touch devices keep :hover after a tap, so the hover states stay on desktop. */}
            {showPrompt ? (
              <span className="absolute inset-1 flex items-center justify-center rounded-full transition-colors md:group-hover:bg-black/30">
                <ImagePhotoAddIcon className="size-9 pl-1 pt-1.5 text-gray-700 transition-colors md:group-hover:text-white" />
              </span>
            ) : (
              <span className="absolute inset-0 hidden items-center justify-center rounded-full bg-black/30 opacity-0 transition-opacity md:flex md:group-hover:opacity-100">
                <CameraIcon className="size-5 text-white" aria-hidden="true" />
              </span>
            )}
          </span>
        </button>
      </div>

      <p className="text-center text-xs text-gray-500">{t('community.profile_identity.avatar_helper')}</p>

      <input {...inputProps} />
    </div>
  );
};

// Streamline ImagePhotoAdd, copied from the Swarm icon set (client/src/ui/icons/streamline).
const ImagePhotoAddIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M6 10.5H1.5C1.23478 10.5 0.98043 10.3946 0.792893 10.2071C0.605357 10.0196 0.5 9.76522 0.5 9.5V1.5C0.5 1.23478 0.605357 0.98043 0.792893 0.792893C0.98043 0.605357 1.23478 0.5 1.5 0.5H9.5C9.76522 0.5 10.0196 0.605357 10.2071 0.792893C10.3946 0.98043 10.5 1.23478 10.5 1.5V6" />
    <path d="M7.125 4.875C6.43464 4.875 5.875 4.31536 5.875 3.625C5.875 2.93464 6.43464 2.375 7.125 2.375C7.81536 2.375 8.375 2.93464 8.375 3.625C8.375 4.31536 7.81536 4.875 7.125 4.875Z" />
    <path d="M0.5 7.07343L3.10468 4.46875L6.94296 8.30704" />
    <path d="M10.75 8V13.5" />
    <path d="M8 10.75H13.5" />
  </svg>
);
